// client/src/context/CartContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import userService from '../services/userService';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { isAuthenticated, user, loading: authLoading } = useAuth();

  // wishlistIds  → Set of trip ID strings (for O(1) lookup)
  // wishlistTrips → full Trip objects (for rendering a wishlist page)
  const [wishlistIds,   setWishlistIds]   = useState(new Set());
  const [wishlistTrips, setWishlistTrips] = useState([]);
  const [loadingWish,   setLoadingWish]   = useState(false);

  /* ── Load wishlist from server whenever the logged-in user changes ── */
  useEffect(() => {
    // Wait for AuthContext to finish rehydrating from localStorage
    if (authLoading) return;

    if (!isAuthenticated || !(user?._id || user?.id)) {
      // Logged out — wipe local state immediately
      setWishlistIds(new Set());
      setWishlistTrips([]);
      return;
    }

    let cancelled = false;

    const fetchWishlist = async () => {
      setLoadingWish(true);
      try {
        const res = await userService.getWishlist();
        if (!cancelled && res.success) {
          const trips = res.wishlist || [];
          setWishlistTrips(trips);
          setWishlistIds(new Set(trips.map((t) => (t._id || t.id || t.tripId)?.toString())));
        }
      } catch (err) {
  if (err?.response?.status !== 401) console.error('Failed to load wishlist:', err);
} finally {
        if (!cancelled) setLoadingWish(false);
      }
    };

    fetchWishlist();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?._id, user?.id, authLoading]);   // ← re-runs on account switch

  /* ── Toggle (add / remove) ── */
  const toggleWishlist = useCallback(
    async (trip) => {
      if (!isAuthenticated) return;   // caller can redirect to login

      const id = (trip._id || trip.id || trip.tripId)?.toString();
      const alreadySaved = wishlistIds.has(id);

      // Optimistic update
      if (alreadySaved) {
        setWishlistIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setWishlistTrips((prev) => prev.filter((t) => t._id?.toString() !== id));
      } else {
        setWishlistIds((prev) => new Set(prev).add(id));
        setWishlistTrips((prev) => [...prev, trip]);
      }

      try {
        if (alreadySaved) {
          await userService.removeFromWishlist(id);
        } else {
          await userService.addToWishlist(id);
        }
      } catch (err) {
        console.error('Wishlist sync error:', err);
        // Roll back optimistic update on failure
        if (alreadySaved) {
          setWishlistIds((prev) => new Set(prev).add(id));
          setWishlistTrips((prev) => [...prev, trip]);
        } else {
          setWishlistIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setWishlistTrips((prev) => prev.filter((t) => t._id?.toString() !== id));
        }
      }
    },
    [isAuthenticated, wishlistIds]
  );

  const isWishlisted = useCallback(
    (tripId) => wishlistIds.has(tripId?.toString()),
    [wishlistIds]
  );

  const value = {
    wishlistIds,
    wishlistTrips,
    loadingWish,
    toggleWishlist,
    isWishlisted,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within <CartProvider>.');
  return ctx;
}

export default CartContext;
